import type ClarityDefault from '@microsoft/clarity';

import {
  CONSENT_CHANGE_EVENT,
  type CookieConsentState,
  readConsent,
} from '@/utils/cookieConsentStorage';
import { Logger } from '@/utils/logging';

import type { TrackingInterface } from '../_types';

// SQR-13012: imported dynamically when consent starts Clarity, keeping the SDK
// out of the entry chunk entirely for non-consented sessions. The `import type`
// above is erased at compile time; only the dynamic import() in startClarity()
// pulls the SDK.
let Clarity: typeof ClarityDefault | null = null;
let isClarityInitialized = false;
let isConsentListenerAttached = false;

export function clarityTracker(
  projectId: string,
  env: string,
): TrackingInterface {
  // Single-flight: two rapid consent events must not double-import/double-init.
  let clarityStartPromise: Promise<void> | null = null;

  // Boot-time identity tags arrive before statistics consent has started
  // Clarity; without a buffer they would be lost for every consented-later
  // session. SQR-14516.
  const pendingTags = new Map<string, string>();

  const startClarity = (): Promise<void> => {
    clarityStartPromise ??= (async () => {
      Logger.console('Initializing Clarity');
      Clarity = (await import('@microsoft/clarity')).default;
      Clarity.init(projectId);
      Clarity.consent(true);
      isClarityInitialized = true;
      const clarity = Clarity;
      pendingTags.forEach((value, key) => clarity.setTag(key, value));
      pendingTags.clear();
    })();
    return clarityStartPromise;
  };

  const initialize = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    // Only load Clarity (which sets third-party cookies) once statistics
    // consent is granted — avoids unconsented cookies flagged by the
    // best-practices audit. If consent isn't granted yet, wait for the
    // consent-change event and start then. See SQR-13012.
    if (readConsent()?.statistics === true) {
      void startClarity();
    }

    if (isConsentListenerAttached) {
      return;
    }
    isConsentListenerAttached = true;

    window.addEventListener(CONSENT_CHANGE_EVENT, (event) => {
      const state = (event as CustomEvent<CookieConsentState>).detail;
      if (state.statistics === true) {
        if (isClarityInitialized) {
          // Re-grant after an in-session revoke: Clarity is already running,
          // so flip consent back on (startClarity would no-op the promise and
          // leave consent off).
          Clarity?.consent(true);
        } else {
          void startClarity();
        }
      } else if (isClarityInitialized) {
        Clarity?.consent(false);
      }
    });
  };

  const setUserId: TrackingInterface['setUserId'] = (userId, context) => {
    // Clarity.init() is what defines window.clarity; every wrapper method is
    // a bare window.clarity(...) call, so calling any of them before consent
    // has started Clarity throws a TypeError. No-op until initialized — the
    // same events keep flowing to the other trackers. See SQR-13012.
    if (!isClarityInitialized || !Clarity) {
      return;
    }
    const clarity = Clarity;

    clarity.identify(userId);
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        if (value) {
          clarity.setTag(key, String(value));
        }
      });
    }
    clarity.setTag('environment', env);
  };

  const setTags: TrackingInterface['setTags'] = (tags) => {
    Object.entries(tags).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      if (isClarityInitialized && Clarity) {
        Clarity.setTag(key, String(value));
      } else {
        pendingTags.set(key, String(value));
      }
    });
  };

  const track: TrackingInterface['track'] = (message, context) => {
    if (!isClarityInitialized || !Clarity) {
      return;
    }
    const clarity = Clarity;

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        if (value) {
          clarity.setTag(key, String(value));
        }
      });
    }
    clarity.event(message);
  };

  const trackScreenName: TrackingInterface['trackScreenName'] = (name) => {
    if (!isClarityInitialized || !Clarity) {
      return;
    }

    Clarity.event(`PageView: ${name}`);
  };

  const getTrackingSessionUrl: TrackingInterface['getTrackingSessionUrl'] =
    async () => {
      // Web version of Clarity doesn't support session URLs
      return undefined;
    };

  return Object.freeze({
    initialize,
    setUserId,
    setTags,
    track,
    trackScreenName,
    getTrackingSessionUrl,
  });
}
